'use client';

import React from 'react';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { Input } from '@packages/shared/src/ui/Input/Input';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import type { SubmitHandler } from 'react-hook-form';
import { useForm, useFormContext } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

interface ProductEditDialogProps {
  open: boolean;
  onClose: () => void;
}

export const productEditSchema = z.object({
  productOrderNumber: z.string().nonempty(),
  productName: z.string().nonempty(),
});

export const productEditMessages = defineMessages({
  productOrderNumberLabel: {
    id: 'ReclamationForm.productOrderNumber',
    defaultMessage: 'Artikelnummer',
  },
  productNameLabel: {
    id: 'ReclamationForm.productName',
    defaultMessage: 'Artikelbeschreibung',
  },
  mandatoryField: {
    id: 'ReclamationForm.mandatoryField',
    defaultMessage: 'Dies ist ein Pflichtfeld',
  },
});

const ProductEditDialog: React.FC<ProductEditDialogProps> = ({ open, onClose }) => {
  const intl = useIntl();
  // access parent form context
  const { getValues: getParentValues, setValue: setParentValue } = useFormContext();

  // validate this form by calling an own useForm hook
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof productEditSchema>>({
    resolver: zodResolver(productEditSchema),
    defaultValues: {
      productOrderNumber: getParentValues('productEditForm.productOrderNumber'),
      productName: getParentValues('productEditForm.productName'),
    },
    mode: 'onBlur',
  });

  // if this form is valid, move values to parent form state
  // and close the dialog
  const onSubmit: SubmitHandler<z.infer<typeof productEditSchema>> = (data) => {
    setParentValue('productEditForm.productOrderNumber', data.productOrderNumber);
    setParentValue('productEditForm.productName', data.productName);
    onClose();
  };

  return (
    <Dialog isOpen={open} onClose={onClose}>
      <DialogHeader onClose={onClose}>
        <FormattedMessage id="ReclamationForm.productEditTitle" defaultMessage="Daten ändern" />
      </DialogHeader>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="bg-color-grey-light p-2">
            <Input
              {...register('productOrderNumber')}
              error={!!errors.productOrderNumber?.message}
              helperText={
                errors.productOrderNumber
                  ? intl.formatMessage(productEditMessages.mandatoryField)
                  : undefined
              }
              label={intl.formatMessage(productEditMessages.productOrderNumberLabel)}
              className="bg-color-background-base"
              name="productOrderNumber"
              defaultValue={getParentValues('productEditForm.productOrderNumber')}
            />
            <Input
              {...register('productName')}
              error={!!errors.productName?.message}
              helperText={
                errors.productName
                  ? intl.formatMessage(productEditMessages.mandatoryField)
                  : undefined
              }
              label={intl.formatMessage(productEditMessages.productNameLabel)}
              className="bg-color-background-base"
              name="productName"
              defaultValue={getParentValues('productEditForm.productName')}
            />
          </div>
          <div className="mt-4 flex justify-center">
            <Button type="submit" color="primary">
              <FormattedMessage
                id="ReclamationForm.productEditButton"
                defaultMessage="Änderung übernehmen"
              />
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { ProductEditDialog };
